import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { caseService } from '@services/api/caseService';
import { useAuthStore } from '@stores/authStore';
import { getCaseStatusLabel, getCaseStatusColor, getCasePriorityLabel, getCasePriorityColor, calculateSLAStatus } from '@/types/case';
import './CaseQueue.css';

export const CaseQueue = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const filter = searchParams.get('filter') || 'all';

  useEffect(() => {
    loadCases();
  }, [filter]);

  const loadCases = async () => {
    setLoading(true);
    const result = await caseService.getCases({
      status: 'all',
      assigned_to: filter === 'assigned_to_me' ? 'me' : filter === 'unassigned' ? 'unassigned' : 'all',
    });

    if (result.success && result.data) {
      let filteredCases = result.data;
      
      // Filter assigned to me
      if (filter === 'assigned_to_me' && user) {
        filteredCases = filteredCases.filter((c: any) => c.assigned_to === user.id);
      }
      
      setCases(filteredCases);
    }
    
    setLoading(false);
  };

  const tabs = [
    { id: 'all', label: 'All Cases', count: cases.length },
    { id: 'assigned_to_me', label: 'Assigned to Me', count: cases.filter(c => c.assigned_to === user?.id).length },
    { id: 'unassigned', label: 'Unassigned', count: cases.filter(c => !c.assigned_to).length },
  ];

  return (
    <div className="case-queue">
      <div className="case-queue-header">
        <div>
          <h1 className="page-title">Cases Management</h1>
          <p className="page-subtitle">{cases.length} cases found</p>
        </div>
        <Link to="/cases/new" className="btn btn-primary">+ Create New Case</Link>
      </div>

      <div className="case-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`case-tab ${filter === tab.id ? 'case-tab-active' : ''}`}
            onClick={() => setSearchParams({ filter: tab.id })}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div>Loading cases...</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Case ID</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th>SLA</th>
                <th>Assigned To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => {
                const slaStatus = calculateSLAStatus(c.created_at, c.sla_due_at, c.status);
                
                return (
                  <tr key={c.id}>
                    <td className="customer-id">#{c.id.slice(0, 8)}</td>
                    <td>
                      {c.customer ? (
                        <Link to={`/customers/${c.customer.id}`} className="customer-name-link">
                          {c.customer.first_name} {c.customer.last_name}
                        </Link>
                      ) : 'N/A'}
                    </td>
                    <td className="text-capitalize text-small">{c.case_type.replace(/_/g, ' ')}</td>
                    <td>
                      <span className={`priority-badge priority-${getCasePriorityColor(c.priority)}`}>
                        {getCasePriorityLabel(c.priority)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${getCaseStatusColor(c.status)}`}>
                        {getCaseStatusLabel(c.status)}
                      </span>
                    </td>
                    <td className="text-small">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td>
                      {c.status !== 'closed' && c.sla_due_at && (
                        <span className={`sla-badge sla-${slaStatus.status}`}>
                          {slaStatus.status === 'overdue' ? 'Overdue' : `${Math.round(slaStatus.hours_remaining)}h`}
                        </span>
                      )}
                    </td>
                    <td className="text-small">
                      {c.assigned_to_staff ? 
                        `${c.assigned_to_staff.first_name} ${c.assigned_to_staff.last_name}` : 
                        <span className="text-secondary">Unassigned</span>}
                    </td>
                    <td>
                      <Link to={`/cases/${c.id}`} className="btn btn-ghost btn-small">View</Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

